import { type SearchJobsQuery } from '@/features/jobs/list/SearchableJobStore'
import type { CursorPaged } from '@/infra/paging'
import { Id } from '@/infra/id'

/**
 * Short IDs are 11 characters drawn from an alphabet that omits the
 * easily-confused `0`, `O`, and `I`. Job IDs are UUIDs, so the two shapes
 * never overlap and a route parameter can be classified with certainty.
 */
const SHORT_ID_PATTERN = /^[A-HJ-NP-Z1-9]{11}$/i

/**
 * Whether `value` has the shape of a job short ID.
 */
export function isJobShortId(value: string | undefined): value is string {
  return !!value && SHORT_ID_PATTERN.test(value)
}

/**
 * Normalizes a short ID for comparison and search. Short IDs are always
 * generated uppercase and are indexed as a keyword.
 */
export function normalizeJobShortId(shortId: string): string {
  return shortId.toUpperCase()
}

/**
 * The fields `resolveJobShortId` needs from a search hit. `SearchableJob`
 * satisfies this structurally.
 */
interface JobShortIdMatch {
  id: Id
  shortId: string
}

/**
 * Searches jobs on behalf of `resolveJobShortId`.
 */
export type FindJobsForShortIdFn = (
  query: SearchJobsQuery,
) => Promise<Pick<CursorPaged<JobShortIdMatch>, 'data'>>

/**
 * Resolves a job short ID to its canonical job ID, or `Id.none` when no job
 * the current user can see has that short ID.
 *
 * There is no `GetJobByShortId` RPC and `short_id` is not a filterable facet,
 * but `searchText` runs an exact match against the indexed short ID, so a
 * single-hit search answers the question.
 */
export async function resolveJobShortId(
  findJobs: FindJobsForShortIdFn,
  shortId: string,
): Promise<Id> {
  const normalized = normalizeJobShortId(shortId)
  const { data } = await findJobs({
    searchText: normalized,
    limit: 1,
    scope: 'All',
  })

  // `searchText` also matches names and emails, so confirm the hit really is
  // the job we asked for rather than trusting the top result.
  const [job] = data
  return job && normalizeJobShortId(job.shortId) === normalized
    ? job.id
    : Id.none
}

/**
 * Thrown when no job the current user can see has the route's short ID.
 */
export class JobShortIdNotFoundError extends Error {
  constructor(readonly shortId: string) {
    super(`No job found with ID ${shortId}.`)
    this.name = 'JobShortIdNotFoundError'
  }
}

/**
 * Thrown when looking up the job for the route's short ID failed, which
 * retrying may fix.
 */
export class JobShortIdLookupError extends Error {
  constructor(cause: unknown) {
    super('Looking up the job by its short ID failed', { cause })
    this.name = 'JobShortIdLookupError'
  }
}
